import { HubConnectionState } from "@microsoft/signalr";
import { useCallback, useEffect, useReducer } from "react";

import { useHubConnection } from "@src/modules/App/hooks/useHubConnection";
import {
  BASE_NOTIFICATION_HEIGHT,
  NOTIFICATIONS_CROP,
} from "@src/modules/App/Notifications/shared/consts";
import { PageableList } from "@src/modules/App/Notifications/shared/pageableList";
import { INotification } from "@src/modules/App/Notifications/shared/types";
import { ActionTypes } from "@src/modules/App/Notifications/signalR/reducer/actions";
import {
  initialNotificationsState,
  reducer,
} from "@src/modules/App/Notifications/signalR/reducer/reducer";
import { useEmitters } from "@src/modules/App/Notifications/signalR/useEmitters";
import { useListeners } from "@src/modules/App/Notifications/signalR/useListeners";
import { useQueryClient } from "react-query";

export interface NotificationsData {
  notifications?: INotification[];
  currentPage?: PageableList<INotification>;
  unreadFlag: boolean;
  markAsRead: (notificationId?: string) => void;
  getNextPage: (pageSize: number) => void;
  setUnreadFlag: (unreadFlag: boolean) => void;
}

export const useNotificationsCore = (): NotificationsData => {
  const { hub: notificationsHub } = useHubConnection();
  const [{ notifications, currentPage, unreadFlag }, dispatch] = useReducer(
    reducer,
    initialNotificationsState,
  );
  const { registerListeners, deregisterListeners } = useListeners();
  const { getNotificationsEmitter, markReadEmitter } =
    useEmitters(notificationsHub);

  const queryClient = useQueryClient();

  useEffect(() => {
    dispatch({ type: ActionTypes.ClearNotifications });

    if (!notificationsHub) return;
    const pageSize =
      Math.ceil(window.innerHeight / BASE_NOTIFICATION_HEIGHT) -
      NOTIFICATIONS_CROP;

    let isMounted = true;

    const startHub = async () => {
      if (notificationsHub.state === HubConnectionState.Disconnected) {
        try {
          await notificationsHub.start();

          if (isMounted) {
            await getNotificationsEmitter({ pageNumber: 1, pageSize });
          }
        } catch (e) {
          console.error("SignalR connection failed: ", e);
        }
      } else if (notificationsHub.state === HubConnectionState.Connected) {
        // edge case if hub was opened for any reason
        if (isMounted) {
          await getNotificationsEmitter({ pageNumber: 1, pageSize });
        }
      }
    };

    registerListeners(notificationsHub, dispatch);
    startHub();

    const onReconnected = () => {
      getNotificationsEmitter({ pageNumber: 1, pageSize });
      queryClient.invalidateQueries();
    };
    notificationsHub.onreconnected(onReconnected);

    return () => {
      isMounted = false;
      deregisterListeners(notificationsHub);
    };
  }, [
    notificationsHub,
    registerListeners,
    deregisterListeners,
    getNotificationsEmitter,
    queryClient,
  ]);

  const markAsRead = useCallback(
    async (notificationId?: string) => await markReadEmitter(notificationId),
    [markReadEmitter],
  );

  const setUnreadFlag = useCallback(
    (unreadFlag: boolean) =>
      dispatch({ type: ActionTypes.SetUnreadFlag, unreadFlag }),
    [],
  );

  const getNextPage = useCallback(
    async (pageSize: number) => {
      if (!currentPage) return;
      await getNotificationsEmitter({
        pageNumber: currentPage?.currentPage + 1,
        pageSize,
      });
    },
    [getNotificationsEmitter, currentPage],
  );

  return {
    notifications,
    currentPage,
    unreadFlag,
    markAsRead,
    getNextPage,
    setUnreadFlag,
  };
};
